<script setup lang="ts">
/**
 * Full-screen mobile overlay for a single payment detail view.
 *
 * Rendered as a fixed-position layer (z-index 10, above MobileBottomNav's 5)
 * via Teleport. iOS-style navbar provides back navigation and an Edit button.
 *
 * Edit opens AddPaymentFormMobileScreen via the Pinia store. App.vue renders
 * that form (z-index 12) when addPaymentFormStore.isOpen && isMobile, so it
 * covers this screen until the user saves or cancels.
 *
 * Polling adapts to payment status: identified payments poll slowly (300 s),
 * unidentified poll at 15 s so staff see resolution promptly — same as the
 * desktop PaymentCard.
 *
 * Edit is intentionally called without clearInfoViewSelections because that
 * helper also clears paymentCardStore.selectedPaymentId, which would dismiss
 * this screen before the form can overlay it.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { deletePayment, fetchPayment, paymentAmountMinorUnits, type Payment } from '@/api/payments'
import {
  paymentCardIdentifiedPollingInterval,
  paymentCardUnidentifiedPollingInterval,
} from '@/config/api'
import { formatDateTime } from '@/utils/formatDateTime'
import { formatMoney } from '@/utils/formatMoney'
import { formatName } from '@/utils/formatName'
import { useAddPaymentFormStore } from '@/stores/add_payment_form_store'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import { usePermissions } from '@/composables/usePermissions'
import { usePaymentSubscription } from '@/composables/usePaymentSubscription'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  paymentId: string
}>()

const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.PaymentsEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.PaymentsDelete))

const emit = defineEmits<{
  close: []
}>()

const IDENTIFIED_STATUS = 'IDENTIFIED'
const { t } = useI18n()

const paymentQuery = useQuery({
  queryKey: computed(() => ['payment', props.paymentId]),
  queryFn: () => fetchPayment(props.paymentId),
  refetchInterval: (query) =>
    query.state.data?.payment_status === IDENTIFIED_STATUS
      ? paymentCardIdentifiedPollingInterval
      : paymentCardUnidentifiedPollingInterval,
  refetchIntervalInBackground: false,
})

const payment = computed<Payment | null>(() => paymentQuery.data.value ?? null)

const subscription = usePaymentSubscription(payment)

const isIdentified = computed(() => payment.value?.payment_status === IDENTIFIED_STATUS)

const displayName = computed<string>(() => {
  const raw = payment.value?.person_name?.trim()
  if (raw) return formatName(raw)
  const personId = payment.value?.person_id
  return personId ? t('common.personFallback', { id: personId }) : '—'
})

const dateLabel = computed<string>(() => {
  const raw = payment.value?.date
  if (!raw) return '—'
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  return formatDateTime({ date: d, dateStyle: 'medium' }).formattedDate
})

const amountNumber = computed<string>(() => {
  const p = payment.value
  if (!p) return '—'
  const minor = paymentAmountMinorUnits(p)
  if (minor === undefined) return '—'
  return formatMoney({ amount: minor }).formatted
})

const amountPositive = computed<boolean>(() => {
  const p = payment.value
  if (!p) return false
  const minor = paymentAmountMinorUnits(p)
  return minor !== undefined && minor > 0
})

const linkHref = computed<string | null>(() => {
  const raw = payment.value?.link?.trim()
  return raw || null
})

const isMailLink = computed<boolean>(() => linkHref.value?.startsWith('mailto:') ?? false)

const bodyText = computed<string | null>(() => payment.value?.text?.trim() || null)

const statusLabel = computed<string>(() => payment.value?.payment_status ?? '')

const addPaymentFormStore = useAddPaymentFormStore()

function openEdit(): void {
  const p = payment.value
  if (!p) return
  addPaymentFormStore.openForEdit(p.id, {
    personId: p.person_id,
    personName: p.person_name ?? null,
    amount: p.amount,
    date: p.date,
    link: p.link ?? null,
    transactionId: p.transaction_id ?? null,
    text: p.text ?? null,
    billId: p.bill_id ?? null,
    paymentStatus: p.payment_status,
    subscriptionTypeId: p.subscription_type_id ?? null,
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deletePayment(props.paymentId),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['payments'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() => {
  const who = isIdentified.value ? displayName.value : t('cards.payment.defaultSenderName')
  return t('cards.payment.confirmDeleteDescription', { amount: amountNumber.value, who })
})

function handleDelete(): void {
  if (!payment.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="pcm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="pcm-navbar">
        <button type="button" class="pcm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('cards.payment.backLabel') }}
        </button>
        <span class="pcm-nav-title">{{ t('cards.payment.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="pcm-nav-edit"
          :disabled="!payment"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <!-- Loading / error states -->
      <div v-if="paymentQuery.isPending.value" class="pcm-state">
        {{ t('cards.payment.loading') }}
      </div>
      <div v-else-if="paymentQuery.isError.value" class="pcm-state pcm-state--error">
        {{ t('cards.payment.loadErrorShort') }}
      </div>

      <!-- Payment content -->
      <div v-else-if="payment" class="pcm-scroll">
        <!-- Hero: amount + method tile -->
        <div class="pcm-hero">
          <div class="pcm-hero-amount" :class="{ 'pcm-hero-amount--positive': amountPositive }">
            <span class="pcm-amount-sign" v-if="amountPositive">+</span>{{ amountNumber }}
          </div>
          <div class="pcm-method-tile">
            <!-- Generic bank/payment icon -->
            <svg
              width="28"
              height="22"
              viewBox="0 0 28 22"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="2" y="8" width="24" height="12" rx="1" />
              <path d="M2 12h24" />
              <path d="M6 16h4M16 16h6" />
              <path d="M14 2L2 8h24L14 2z" />
            </svg>
            <span class="pcm-method-label">{{ t('cards.payment.bankTransfer') }}</span>
          </div>
        </div>

        <!-- Status banner -->
        <div v-if="isIdentified" class="pcm-status pcm-status--ok">
          <svg
            width="16"
            height="16"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="pcm-status-icon"
            aria-hidden="true"
          >
            <path d="M2.5 7.5l3 3 6-6" />
          </svg>
          <div>
            <div class="pcm-status-title">
              {{ t('cards.payment.identifiedLabel', { name: displayName }) }}
            </div>
          </div>
        </div>
        <div v-else class="pcm-status pcm-status--err">
          <svg
            width="16"
            height="16"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="pcm-status-icon"
            aria-hidden="true"
          >
            <path d="M7 1L13 12H1L7 1z" />
            <path d="M7 5.5v3M7 10v.5" />
          </svg>
          <div>
            <div class="pcm-status-title">{{ t('cards.payment.notIdentifiedLabel') }}</div>
            <div v-if="statusLabel" class="pcm-status-reason">
              {{ t('cards.payment.statusLabel', { status: statusLabel }) }}
            </div>
            <button v-if="canEdit" type="button" class="pcm-status-action" @click="openEdit">
              {{ t('cards.payment.identifyClient') }}
            </button>
          </div>
        </div>

        <!-- Details -->
        <div class="pcm-section-label">{{ t('cards.payment.details') }}</div>
        <div class="pcm-group">
          <div class="pcm-meta-row">
            <span class="pcm-meta-key">{{ t('common.client') }}</span>
            <span class="pcm-meta-val">{{ isIdentified ? displayName : '—' }}</span>
          </div>
          <div class="pcm-meta-row">
            <span class="pcm-meta-key">{{ t('common.date') }}</span>
            <span class="pcm-meta-val pcm-meta-val--mono">{{ dateLabel }}</span>
          </div>
          <div class="pcm-meta-row">
            <span class="pcm-meta-key">{{ t('cards.payment.subscription') }}</span>
            <span v-if="subscription.info.value" class="pcm-subscription-val">
              <span class="pcm-subscription-tag">{{ subscription.info.value.tag }}</span>
              <span v-if="subscription.info.value.detail" class="pcm-subscription-sub">{{
                subscription.info.value.detail
              }}</span>
            </span>
            <span v-else class="pcm-subscription-none">{{
              t('cards.payment.subscriptionNotLinked')
            }}</span>
          </div>
          <div v-if="payment.transaction_id" class="pcm-meta-row pcm-meta-row--last">
            <span class="pcm-meta-key">{{ t('cards.payment.transaction') }}</span>
            <span class="pcm-meta-val pcm-meta-val--mono">{{ payment.transaction_id }}</span>
          </div>
          <div v-if="payment.bill_id" class="pcm-meta-row pcm-meta-row--last">
            <span class="pcm-meta-key">{{ t('cards.payment.bill') }}</span>
            <span class="pcm-meta-val pcm-meta-val--mono">{{ payment.bill_id }}</span>
          </div>
        </div>

        <!-- Source link -->
        <template v-if="linkHref">
          <div class="pcm-section-label">{{ t('cards.payment.source') }}</div>
          <div class="pcm-group">
            <div class="pcm-source-row">
              <!-- Mail icon -->
              <svg
                v-if="isMailLink"
                width="15"
                height="15"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="pcm-source-icon"
                aria-hidden="true"
              >
                <rect x="1" y="3" width="12" height="8" rx="1" />
                <path d="M1 4l6 4 6-4" />
              </svg>
              <!-- Link icon -->
              <svg
                v-else
                width="15"
                height="15"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="pcm-source-icon"
                aria-hidden="true"
              >
                <path d="M5.5 8.5a3.5 3.5 0 0 0 5 0l1.5-1.5a3.5 3.5 0 0 0-5-5L5.5 3.5" />
                <path d="M8.5 5.5a3.5 3.5 0 0 0-5 0L2 7a3.5 3.5 0 0 0 5 5l1.5-1.5" />
              </svg>
              <a :href="linkHref" target="_blank" rel="noopener noreferrer">{{ linkHref }}</a>
            </div>
          </div>
        </template>

        <!-- Notification text -->
        <template v-if="bodyText">
          <div class="pcm-section-label">{{ t('cards.payment.notificationText') }}</div>
          <div class="pcm-raw-body">{{ bodyText }}</div>
        </template>

        <!-- Grows to push Delete to the bottom of the screen when content is
             short; shrinks to 0 and lets the normal scroll take over once
             content overflows the viewport. -->
        <div class="pcm-fill" />

        <!-- Delete -->
        <div v-if="canDelete" class="pcm-danger-group">
          <button
            type="button"
            class="pcm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="pcm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.payment.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.pcm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
.pcm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.pcm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.pcm-nav-back:active {
  opacity: 0.6;
}

.pcm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.pcm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.pcm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.pcm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

/* ── Loading / error states ───────────────────────────────────────────────── */
.pcm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.pcm-state--error {
  color: var(--color-money-negative);
}

/* ── Scrollable content ───────────────────────────────────────────────────── */
.pcm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Hero ─────────────────────────────────────────────────────────────────── */
.pcm-hero {
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 1.4rem 1.25rem 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.pcm-hero-amount {
  font-family: var(--font-tufte-mono);
  font-size: 2.6rem;
  font-weight: 600;
  line-height: 1;
  color: var(--color-tufte-muted);
}
.pcm-hero-amount--positive {
  color: var(--color-money-positive);
}

.pcm-amount-sign {
  font-size: 2rem;
}

.pcm-method-tile {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-tufte-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--color-tufte-border) 20%, var(--color-tufte-bg));
  color: var(--color-tufte-muted);
}

.pcm-method-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

/* ── Status banner ────────────────────────────────────────────────────────── */
.pcm-status {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.8rem 1.25rem;
  border-bottom: 1px solid var(--color-tufte-border);
}
.pcm-status--ok {
  background: var(--color-surface-success);
  border-bottom-color: color-mix(in srgb, var(--color-money-positive) 25%, transparent);
}
.pcm-status--err {
  background: var(--color-surface-danger);
  border-bottom-color: color-mix(in srgb, var(--color-money-negative) 20%, transparent);
}

.pcm-status-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
}
.pcm-status--ok .pcm-status-icon {
  color: var(--color-money-positive);
}
.pcm-status--err .pcm-status-icon {
  color: var(--color-money-negative);
}

.pcm-status-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pcm-status--ok .pcm-status-title {
  color: var(--color-money-positive);
}
.pcm-status--err .pcm-status-title {
  color: var(--color-money-negative);
}

.pcm-status-reason {
  font-family: var(--font-tufte-mono);
  font-size: 0.78rem;
  color: var(--color-text-danger-muted);
  margin-top: 0.25rem;
  line-height: 1.4;
}

.pcm-status-action {
  margin-top: 0.55rem;
  font: inherit;
  font-size: 0.82rem;
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid color-mix(in srgb, var(--color-money-negative) 40%, transparent);
  border-radius: 7px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  min-height: 40px;
}
.pcm-status-action:active {
  opacity: 0.7;
}

/* ── Section labels ───────────────────────────────────────────────────────── */
.pcm-section-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  padding: 0 1.25rem 0.4rem;
  margin-top: 1.2rem;
}

/* ── Group card ───────────────────────────────────────────────────────────── */
.pcm-group {
  background: var(--color-tufte-bg);
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
}

/* ── Meta rows ────────────────────────────────────────────────────────────── */
.pcm-meta-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 1.25rem;
  border-bottom: 1px solid #eeeee8;
}
.pcm-meta-row:last-child,
.pcm-meta-row--last {
  border-bottom: none;
}

.pcm-subscription-val {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
}

.pcm-subscription-tag {
  display: inline-block;
  background: var(--color-subscription-band);
  color: var(--color-subscription-ink);
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  padding: 0.1rem 0.42rem;
  white-space: nowrap;
}

.pcm-subscription-sub {
  font-family: var(--font-tufte-sans);
  font-size: 0.74rem;
  color: var(--color-tufte-muted);
}

.pcm-subscription-none {
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  color: var(--color-tufte-muted);
}

.pcm-meta-key {
  font-family: var(--font-tufte-sans);
  font-size: 0.74rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex-shrink: 0;
}

.pcm-meta-val {
  font-size: 1rem;
  text-align: right;
  word-break: break-all;
}
.pcm-meta-val--mono {
  font-family: var(--font-tufte-mono);
  font-size: 0.9rem;
}

/* ── Source row ───────────────────────────────────────────────────────────── */
.pcm-source-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1.25rem;
}

.pcm-source-icon {
  flex-shrink: 0;
  color: var(--color-tufte-muted);
}

.pcm-source-row a {
  font-family: var(--font-tufte-mono);
  font-size: 0.8rem;
  color: var(--color-tufte-text);
  text-decoration: underline;
  text-underline-offset: 0.15em;
  word-break: break-all;
}

/* ── Raw notification body ────────────────────────────────────────────────── */
.pcm-raw-body {
  font-family: var(--font-tufte-mono);
  font-size: 0.78rem;
  line-height: 1.6;
  color: var(--color-tufte-secondary);
  background: var(--color-tufte-bg);
  padding: 0.9rem 1.25rem 1.1rem;
  white-space: pre-wrap;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
}

.pcm-fill {
  flex: 1;
}

/* ── Danger group ────────────────────────────────────────────────────────── */
.pcm-danger-group {
  margin-top: 1.4rem;
}

.pcm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.pcm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.pcm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.pcm-scroll-end {
  height: 2.5rem;
}
</style>
